import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, InputAdornment, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FONT_FAMILY } from '../../Constant';
import { useDispatch } from 'react-redux';
import { saveShopifyKeys } from '../../redux/actions/shopifyActions';
import { useSelector } from 'react-redux';
import { showError, showSuccess } from '../../Components/reuse/Toast';

function ShopifyDialog({ open, onClose, onConnect, initialValues = null, readOnly = false }) {
  const dispatch = useDispatch();
  const initialForm = useMemo(() => ({ storeUrl: '', accessToken: '', appSecretKey: '' }), []);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [copied, setCopied] = useState({ storeUrl: false, accessToken: false, appSecretKey: false });
  const loading = useSelector((s) => s.shopify.loading);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const parseShopifyStoreUrl = (value) => {
    try {
      const trimmed = String(value || '').trim();
      if (!trimmed) return { valid: false };
      const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      const url = new URL(withProtocol);
      const host = url.hostname.toLowerCase();
      if (!host.endsWith('.myshopify.com')) return { valid: false };
      const subdomain = host.replace('.myshopify.com', '');
      if (!/^[a-z0-9-]+$/i.test(subdomain)) return { valid: false };
      const normalized = `https://${subdomain}.myshopify.com/`;
      return { valid: true, normalized };
    } catch (_) {
      return { valid: false };
    }
  };

  const validate = () => {
    const newErr = {};
    const parsed = parseShopifyStoreUrl(form.storeUrl);
    if (!parsed.valid) newErr.storeUrl = 'Please provide a valid Shopify store URL (e.g., https://yourstore.myshopify.com/)';

    if (!form.accessToken) newErr.accessToken = 'Access token is required';
    else if (String(form.accessToken).length < 10) newErr.accessToken = 'Access token must be at least 10 characters long';

    if (!form.appSecretKey) newErr.appSecretKey = 'App secret key is required';
    else if (String(form.appSecretKey).length < 10) newErr.appSecretKey = 'App secret key must be at least 10 characters long';
    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const handleConnect = async () => {
    if (!validate()) return;
    try {
      // Use normalized store URL when sending to backend
      const parsed = parseShopifyStoreUrl(form.storeUrl);
      const payload = parsed.valid ? { ...form, storeUrl: parsed.normalized } : form;
      await dispatch(saveShopifyKeys(payload));
      showSuccess('Shopify keys saved');
      onConnect?.(form);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const backendMsg = err?.response?.data?.message;
      const backendError = err?.response?.data?.error;
      
      if (Array.isArray(apiErrors)) {
        const fieldMap = {};
        const messages = [];
        apiErrors.forEach((e) => {
          if (e?.path && e?.msg) {
            fieldMap[e.path] = e.msg;
            messages.push(e.msg);
          }
        });
        setErrors(fieldMap);
        if (messages.length > 0) {
          showError(messages.join('\n'));
          return;
        }
      }
      
      // Handle specific error format from saveshopifykey API
      if (backendError) {
        showError(backendError);
        return;
      }
      
      showError(backendMsg || 'Failed to save Shopify keys');
    }
  };

  const resetForm = useCallback(() => {
    setForm(initialForm);
    setErrors({});
    setShowAccessToken(false);
    setCopied({ storeUrl: false, accessToken: false, appSecretKey: false });
  }, [initialForm]);

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  useEffect(() => {
    if (!open) {
      // Ensure reset when parent closes the dialog
      resetForm();
    } else if (readOnly && initialValues) {
      // Populate from existing saved values when viewing connection
      setForm({
        storeUrl: initialValues.storeUrl || '',
        accessToken: initialValues.accessToken || '',
        appSecretKey: initialValues.appSecretKey || '',
      });
    }
  }, [open, readOnly, initialValues, resetForm]);

  const handleCopy = async (key) => {
    try {
      await navigator.clipboard.writeText(form[key] || '');
      setCopied((s) => ({ ...s, [key]: true }));
      setTimeout(() => setCopied((s) => ({ ...s, [key]: false })), 1200);
    } catch (_) {}
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>Connect Shopify Store</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          margin="dense"
          label="Store URL"
          placeholder="https://yourstore.myshopify.com/"
          name="storeUrl"
          fullWidth
          value={form.storeUrl}
          onChange={handleChange}
          error={!!errors.storeUrl}
          helperText={errors.storeUrl || ''}
          onBlur={(e) => {
            const parsed = parseShopifyStoreUrl(e.target.value);
            if (parsed.valid) setForm((s) => ({ ...s, storeUrl: parsed.normalized }));
          }}
          InputProps={{
            readOnly: readOnly,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied.storeUrl ? 'Copied' : 'Copy'}>
                  <IconButton size="small" onClick={() => handleCopy('storeUrl')} edge="end">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="dense"
          label="Access Token"
          name="accessToken"
          fullWidth
          type={showAccessToken ? 'text' : 'password'}
          value={form.accessToken}
          onChange={handleChange}
          error={!!errors.accessToken}
          helperText={errors.accessToken || ''}
          InputProps={{
            readOnly: readOnly,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied.accessToken ? 'Copied' : 'Copy'}>
                  <IconButton size="small" onClick={() => handleCopy('accessToken')} edge="end">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setShowAccessToken((s) => !s)} edge="end">
                  {showAccessToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="dense"
          label="App Secret Key"
          name="appSecretKey"
          fullWidth
          value={form.appSecretKey}
          onChange={handleChange}
          error={!!errors.appSecretKey}
          helperText={errors.appSecretKey || ''}
          InputProps={{
            readOnly: readOnly,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied.appSecretKey ? 'Copied' : 'Copy'}>
                  <IconButton size="small" onClick={() => handleCopy('appSecretKey')} edge="end">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions>
        {readOnly ? (
          <Button variant="contained" onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button variant="contained" onClick={handleConnect} disabled={loading}>
              {loading ? 'Saving…' : 'Connect'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ShopifyDialog;
